import { Location } from 'history';
import qs, { IParseOptions, IStringifyOptions } from 'qs';
import { omit } from 'lodash';
export enum CommonQueryParams {
  Integration = 'integration',
  Success = 'success',
  Error = 'error',
}
export function parse<T extends Record<string, any>>(q: string | undefined, options?: IParseOptions): T | undefined {
  return q ? qs.parse(q, {
    ignoreQueryPrefix: true,
    ...options
  }) as T : undefined;
}
export const stringify = (q: string | Record<string, any>, options?: IStringifyOptions) => qs.stringify(q, options);
export const getURLCommonParam = <T = string | number,>(location: Location | undefined, param: CommonQueryParams): T | undefined => {
  const parsed = parse(location?.search || window.location.search);
  return parsed?.[param];
};
export const getSuccessParam = (location: Location): string | undefined => getURLCommonParam(location, CommonQueryParams.Success);
export const getErrorParam = (location: Location): string | undefined => getURLCommonParam(location, CommonQueryParams.Error);

/**
 * Removes a single query param from a `location.search` string, returning a new search string
 * (with leading `?`, or empty when nothing remains). Returns the input untouched when the param is
 * absent so callers can skip a needless history entry.
 *
 * The surviving params are re-serialized, so their encoding is normalized. That is the reason this
 * is built on this module's `parse`/`stringify` rather than `URLSearchParams`: both rewrite, but
 * only one rewrites to the `qs` conventions the rest of the app reads them back with.
 */
export const stripSearchParam = (search: string, param: string): string => {
  const params = parse<Record<string, unknown>>(search);
  if (params?.[param] === undefined) {
    return search;
  }
  const next = stringify(omit(params, [param]));
  return next ? `?${next}` : '';
};